import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import { LazyLoadImage } from "react-lazy-load-image-component";
import showErrorToast from "../toast/toast";
import { useProfileAndSettings } from "../../../hooks/useProfileAndSettings";
import { getCurrencySymbol } from "../../../utils/currencyDisplay";
import "../../../pages/loyalty/rules.css";

const getProductId = (product) =>
  product?.menuItem_ID ?? product?.MenuItem_ID ?? product?.id;

const getProductName = (product) =>
  product?.menuItem_Name ?? product?.MenuItem_Name ?? product?.name ?? "Unknown Product";

const getDefaultVariantTitle = (product) =>
  (
    product?.menuItem_DefaultVariantTitle ??
    product?.MenuItem_DefaultVariantTitle ??
    ""
  ).trim();

const getProductImage = (product) =>
  product?.menuItem_Image ??
  product?.MenuItem_Image ??
  product?.parentMenuItem_Image ??
  "/assets/img/default-product.png";

const getProductPrice = (product) =>
  product?.menuItem_SalePrice ??
  product?.MenuItem_SalePrice ??
  product?.menuItem_Price ??
  product?.MenuItem_Price;

const hasVariants = (product) =>
  product?.menuItem_HasVariant === true || product?.MenuItem_HasVariant === true;

const idsEqual = (a, b) => {
  if (a == null || b == null) return false;
  if (a == b) return true;
  if (parseInt(a, 10) === parseInt(b, 10)) return true;
  return String(a).trim() === String(b).trim();
};

const getEntryId = (entry) => {
  if (entry == null) return null;
  if (typeof entry === "object") {
    return getProductId(entry);
  }
  return entry;
};

const buildVariantDisplayName = (parentName, variantName) => {
  const parent = (parentName || "").trim();
  const variant = (variantName || "").trim();
  if (parent && variant) return `${parent} (${variant})`;
  return variant || parent || "Unknown Product";
};

const toSelectableEntry = (product, parent = null) => {
  const productId = getProductId(product);
  const productName = getProductName(product);

  if (parent) {
    const parentId = getProductId(parent);
    const parentName = getProductName(parent);
    const defaultTitle = getDefaultVariantTitle(product);
    return {
      ...product,
      menuItem_ID: productId,
      MenuItem_ID: productId,
      menuItem_Name: productName,
      MenuItem_Name: productName,
      parentMenuItem_ID: parentId,
      ParentMenuItem_ID: parentId,
      parentMenuItem_Name: parentName,
      ParentMenuItem_Name: parentName,
      menuItem_Image:
        product.menuItem_Image ||
        product.MenuItem_Image ||
        parent.menuItem_Image ||
        parent.MenuItem_Image,
      displayName:
        defaultTitle || buildVariantDisplayName(parentName, productName),
    };
  }

  return {
    ...product,
    menuItem_ID: productId,
    MenuItem_ID: productId,
    displayName: productName,
  };
};

const fetchVariantsForParent = async (compID, parent) => {
  const parentId = getProductId(parent);
  const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
    params: { CompID: compID, MenuItem_ID: parentId },
  });
  const ok = response.data?.status || response.data?.Status;
  const data = response.data?.data || response.data?.Data;
  const variants = data?.products || data?.Products || [];
  if (!ok || !Array.isArray(variants)) {
    return [];
  }
  return variants.map((variant) => toSelectableEntry(variant, parent));
};

export default function ProductVariantMultiSelectionModal({
  showModal,
  setShowModal,
  showButton,
  onSubmit,
  setSelectedProducts,
  selectedProducts,
  products,
  compID,
  maxSelection = null,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectableItems, setSelectableItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [tempSelected, setTempSelected] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbol = getCurrencySymbol(getCompCurrency());
  const pickerStyle = { "--loyalty-accent": themeColor };

  useEffect(() => {
    if (!showModal || !compID || !products?.length) {
      if (!showModal) {
        setSelectableItems([]);
        setFilteredItems([]);
      }
      return;
    }

    let cancelled = false;

    const buildList = async () => {
      setListLoading(true);
      try {
        const variantParents = products.filter(hasVariants);
        const plainProducts = products.filter((p) => !hasVariants(p));

        const variantLists = await Promise.all(
          variantParents.map(async (parent) => {
            try {
              return await fetchVariantsForParent(compID, parent);
            } catch {
              return [];
            }
          })
        );

        if (cancelled) return;

        const flattened = [
          ...plainProducts.map((p) => toSelectableEntry(p)),
          ...variantLists.flat(),
        ];

        setSelectableItems(flattened);
        setFilteredItems(flattened);
      } catch {
        if (!cancelled) {
          showErrorToast("Failed to load product list");
          setSelectableItems([]);
          setFilteredItems([]);
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    };

    buildList();

    return () => {
      cancelled = true;
    };
  }, [showModal, products, compID]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems(selectableItems);
      return;
    }

    const term = searchTerm.trim().toLowerCase();
    setFilteredItems(
      selectableItems.filter((item) => {
        const display = (item.displayName || getProductName(item)).toLowerCase();
        const parent = (item.parentMenuItem_Name || item.ParentMenuItem_Name || "").toLowerCase();
        const variant = getProductName(item).toLowerCase();
        return display.includes(term) || parent.includes(term) || variant.includes(term);
      })
    );
  }, [searchTerm, selectableItems]);

  useEffect(() => {
    if (!showModal) {
      setSearchTerm("");
      return;
    }

    const normalized = (selectedProducts || []).map((entry) => {
      if (typeof entry === "object" && entry !== null) {
        return { ...entry };
      }
      const id = parseInt(entry, 10);
      const fromList = selectableItems.find((p) => idsEqual(getProductId(p), id));
      if (fromList) {
        return { ...fromList };
      }
      const parent = (products || []).find((p) => idsEqual(getProductId(p), id));
      if (parent) {
        return toSelectableEntry(parent);
      }
      return { menuItem_ID: id, MenuItem_ID: id, displayName: `Item #${id}` };
    });
    setTempSelected(normalized);
  }, [selectedProducts, showModal, products, selectableItems]);

  const isEntrySelected = useCallback(
    (entryId) => tempSelected.some((entry) => idsEqual(getEntryId(entry), entryId)),
    [tempSelected]
  );

  const handleItemClick = (item) => {
    const itemId = getProductId(item);

    setTempSelected((prev) => {
      const index = prev.findIndex((entry) => idsEqual(getEntryId(entry), itemId));
      if (index > -1) {
        return prev.filter((_, i) => i !== index);
      }
      const entry = { ...item };
      if (maxSelection === 1) {
        return [entry];
      }
      if (maxSelection && prev.length >= maxSelection) {
        return prev;
      }
      return [...prev, entry];
    });
  };

  const handleSubmit = () => {
    const result = tempSelected.map((entry) => ({ ...entry }));
    if (typeof onSubmit === "function") {
      onSubmit(result);
    }
    if (typeof setSelectedProducts === "function") {
      setSelectedProducts(result);
    }
    setShowModal(false);
  };

  const RenderItemCard = ({ item }) => {
    const itemId = getProductId(item);
    const isSelected = isEntrySelected(itemId);
    const label = item.displayName || getProductName(item);
    const dark = isDarkMode ? " loyalty-product-picker-card--dark" : "";
    const selected = isSelected ? " loyalty-product-picker-card--selected" : "";

    return (
      <div
        className={`loyalty-product-picker-card${dark}${selected}`}
        style={pickerStyle}
        onClick={() => handleItemClick(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleItemClick(item);
          }
        }}
      >
        <div className="loyalty-product-picker-card-inner">
          <div className="loyalty-product-picker-thumb">
            <LazyLoadImage src={getProductImage(item)} alt={label} effect="opacity" />
          </div>
          <div className="loyalty-product-picker-card-body">
            <span className="loyalty-product-picker-name">{label}</span>
            {getProductPrice(item) != null && (
              <span className="loyalty-product-picker-price">{currencySymbol}{getProductPrice(item)}</span>
            )}
          </div>
        </div>
        <div className="loyalty-product-picker-check">
          {isSelected && <i className="fa fa-check" />}
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={showModal}
      title="Select Products"
      onClose={() => setShowModal(false)}
      onSubmit={handleSubmit}
      showButton={showButton}
      showSubmitButton={true}
      isCentered={true}
      submitButtonText="Select"
      themeColor={themeColor}
    >
      <div style={pickerStyle}>
        <p
          className={`loyalty-product-picker-desc ${
            isDarkMode ? "loyalty-product-picker-desc--dark" : ""
          }`}
        >
          Products with sizes appear as separate rows (e.g. Chicken Burger (Large)). Select all
          variants you want this rule to apply to.
        </p>

        <div className="loyalty-product-picker-search-wrap">
          <input
            type="text"
            className={`form-control loyalty-product-picker-search ${
              isDarkMode ? "loyalty-product-picker-search--dark" : ""
            }`}
            placeholder="Search by product or variant name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={listLoading}
          />
          <i className="fa fa-search loyalty-product-picker-search-icon" />
        </div>

        {tempSelected.length > 0 && (
          <div
            className={`loyalty-product-picker-count ${
              isDarkMode ? "loyalty-product-picker-count--dark" : ""
            }`}
          >
            {tempSelected.length} item{tempSelected.length === 1 ? "" : "s"} selected
          </div>
        )}

        <div className="loyalty-product-picker-list">
          {listLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p
                className={`mt-2 mb-0 loyalty-product-picker-desc ${
                  isDarkMode ? "loyalty-product-picker-desc--dark" : ""
                }`}
              >
                Loading products...
              </p>
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <RenderItemCard key={getProductId(item)} item={item} />
            ))
          ) : (
            <div
              className={`loyalty-product-picker-empty ${
                isDarkMode ? "loyalty-product-picker-empty--dark" : ""
              }`}
            >
              No products found
              {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  );
}
